import React, { createContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { adminApi, authApi } from '../services/api';
import {
  buildCentralLogoutUrl,
  getCentralAuthOrigin,
  isStandaloneSurbhiApp,
  isSurbhiHostname,
} from '../utils/sso';
import { startSessionGuard } from '../utils/sessionGuard';

const AUTH_COOKIE_SUFFIX = process.env.REACT_APP_AUTH_COOKIE_SUFFIX || '';
const AUTH_USER_COOKIE = `spm_user${AUTH_COOKIE_SUFFIX}`;
const AUTH_TOKEN_COOKIE = `spm_token${AUTH_COOKIE_SUFFIX}`;
// Namespaced like the auth cookies. It is set on the shared .surbhi.net parent
// domain, so without the env suffix a logout on demo would force prompt:'login'
// on prod (and vice versa) — the cross-environment SSO re-prompt bug.
const JUST_LOGGED_OUT_COOKIE = `spm_just_logged_out${AUTH_COOKIE_SUFFIX}`;

function getCookie(name: string): string | null {
  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!cookie) return null;
  return decodeURIComponent(cookie.split('=').slice(1).join('='));
}

function getCookieDomain(): string {
  const host = window.location.hostname;
  if (host.endsWith('.surbhi.net')) return '.surbhi.net';
  return host;
}

function setAuthCookie(name: string, value: string, maxAgeSeconds = 60 * 60 * 24 * 365) {
  const domain = getCookieDomain();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Domain=${domain}; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function clearAuthCookie(name: string) {
  const domain = getCookieDomain();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Path=/; Domain=${domain}; Max-Age=0; SameSite=Lax${secure}`;
}

function clearLocalAuthState() {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  clearAuthCookie(AUTH_USER_COOKIE);
  clearAuthCookie(AUTH_TOKEN_COOKIE);
}

function setJustLoggedOutCookie() {
  const domain = getCookieDomain();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  // Expires in 5 minutes — enough time to survive the logout redirect and prevent auto-relogin
  document.cookie = `${JUST_LOGGED_OUT_COOKIE}=1; Path=/; Domain=${domain}; Max-Age=300; SameSite=Lax${secure}`;
}

function clearJustLoggedOutCookie() {
  const domain = getCookieDomain();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${JUST_LOGGED_OUT_COOKIE}=; Path=/; Domain=${domain}; Max-Age=0; SameSite=Lax${secure}`;
}

function isJwtExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = Number(payload?.exp || 0);
    if (!exp) return false;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

function bootstrapAuthFromStorage(): User | null {
  const savedUser = localStorage.getItem('user');
  const savedToken = localStorage.getItem('token');
  const cookieToken = getCookie(AUTH_TOKEN_COOKIE);
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const requireSharedCookie = isSurbhiHostname(host);

  if (savedUser && savedToken) {
    // Check if token is expired on load
    if (isJwtExpired(savedToken)) {
      console.log('Token expired on app load, clearing session');
      clearLocalAuthState();
      return null;
    }

    // On Surbhi hosts, shared auth cookie is source of truth.
    if (requireSharedCookie) {
      if (!cookieToken || isJwtExpired(cookieToken) || cookieToken !== savedToken) {
        console.log('Shared auth cookie missing or mismatched, clearing local session');
        clearLocalAuthState();
        return null;
      }
    }

    try {
      return JSON.parse(savedUser);
    } catch {
      clearLocalAuthState();
      return null;
    }
  }

  const cookieUser = getCookie(AUTH_USER_COOKIE);
  if (!cookieUser || !cookieToken) {
    return null;
  }

  // Check if token is expired
  if (isJwtExpired(cookieToken)) {
    console.log('Cookie token expired, clearing session');
    clearLocalAuthState();
    return null;
  }

  try {
    const parsedUser = JSON.parse(cookieUser) as User;
    localStorage.setItem('user', JSON.stringify(parsedUser));
    localStorage.setItem('token', cookieToken);
    return parsedUser;
  } catch {
    return null;
  }
}

export interface UserPermissions {
  can_view_products: boolean;
  can_view_tasks: boolean;
  can_view_emd: boolean;
  can_view_insurance?: boolean;
  can_view_sepl?: boolean;
  can_create_sepl?: boolean;
  can_delete_sepl?: boolean;
  can_view_billing_portal?: boolean;
  can_create_billing_portal?: boolean;
  can_delete_billing_portal?: boolean;
  can_view_hr_recruitment?: boolean;
  can_create_hr_recruitment?: boolean;
  can_delete_hr_recruitment?: boolean;
  can_view_cost_sheet?: boolean;
  can_view_letters?: boolean;
  can_create_letters?: boolean;
  can_delete_letters?: boolean;
  is_authorized_signatory?: boolean;
  can_view_tasktracker?: boolean;
  can_create_tasktracker?: boolean;
  can_delete_tasktracker?: boolean;
  is_admin: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  permissions?: UserPermissions;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissionsLoaded: boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  setIsLoading: (loading: boolean) => void;
  fetchPermissions: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => bootstrapAuthFromStorage());

  const [isLoading, setIsLoading] = useState(false);
  // Default to loaded so UI isn’t blocked if we don’t have a token yet
  const [permissionsLoaded, setPermissionsLoaded] = useState(() => {
    const savedUser = localStorage.getItem('user');
    if (!savedUser) return true;
    const user = JSON.parse(savedUser);
    return !!user?.permissions;
  });
  // Validate centralized session on standalone module hosts during bootstrap.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isStandaloneSurbhiApp(window.location.hostname)) return;

    let cancelled = false;

    const checkCentralSession = async () => {
      setIsLoading(true);
      try {
        const response = await authApi.ssoIntrospect();
        const isActive = response?.data?.active === true;
        if (!isActive) {
          clearLocalAuthState();
          if (!cancelled) setUser(null);
        }
      } catch {
        clearLocalAuthState();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    checkCentralSession();

    return () => {
      cancelled = true;
    };
  }, []);

  // Slide the session while active; enforce a short idle timeout.
  useEffect(() => {
    const idleMin = Number(process.env.REACT_APP_IDLE_TIMEOUT_MIN || '30');
    startSessionGuard({
      centralOrigin: getCentralAuthOrigin(),
      idleMs: (Number.isFinite(idleMin) && idleMin > 0 ? idleMin : 30) * 60 * 1000,
    });
  }, []);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPermissionsLoaded(true);
      return;
    }

    try {
      const response = await adminApi.getStatus();

      // Backend returns permissions directly in response.data
      const permissions: UserPermissions = {
        can_view_products:
          response.data.can_view_products === 1 ||
          response.data.can_view_products === true ||
          false,
        can_view_tasks:
          response.data.can_view_tasks === 1 || response.data.can_view_tasks === true || false,
        can_view_emd:
          response.data.can_view_emd === 1 || response.data.can_view_emd === true || false,
        can_view_insurance:
          response.data.can_view_insurance === 1 ||
          response.data.can_view_insurance === true ||
          false,
        can_view_sepl:
          response.data.can_view_sepl === 1 || response.data.can_view_sepl === true || false,
        can_create_sepl:
          response.data.can_create_sepl === 1 || response.data.can_create_sepl === true || false,
        can_delete_sepl:
          response.data.can_delete_sepl === 1 || response.data.can_delete_sepl === true || false,
        can_view_billing_portal:
          response.data.can_view_billing_portal === 1 ||
          response.data.can_view_billing_portal === true ||
          false,
        can_create_billing_portal:
          response.data.can_create_billing_portal === 1 ||
          response.data.can_create_billing_portal === true ||
          false,
        can_delete_billing_portal:
          response.data.can_delete_billing_portal === 1 ||
          response.data.can_delete_billing_portal === true ||
          false,
        can_view_hr_recruitment:
          response.data.can_view_hr_recruitment === 1 ||
          response.data.can_view_hr_recruitment === true ||
          false,
        can_create_hr_recruitment:
          response.data.can_create_hr_recruitment === 1 ||
          response.data.can_create_hr_recruitment === true ||
          false,
        can_delete_hr_recruitment:
          response.data.can_delete_hr_recruitment === 1 ||
          response.data.can_delete_hr_recruitment === true ||
          false,
        can_view_cost_sheet:
          response.data.can_view_cost_sheet === 1 ||
          response.data.can_view_cost_sheet === true ||
          false,
        can_view_letters:
          response.data.can_view_letters === 1 || response.data.can_view_letters === true || false,
        can_create_letters:
          response.data.can_create_letters === 1 ||
          response.data.can_create_letters === true ||
          false,
        can_delete_letters:
          response.data.can_delete_letters === 1 ||
          response.data.can_delete_letters === true ||
          false,
        is_authorized_signatory:
          response.data.is_authorized_signatory === 1 ||
          response.data.is_authorized_signatory === true ||
          false,
        can_view_tasktracker:
          response.data.can_view_tasktracker === 1 ||
          response.data.can_view_tasktracker === true ||
          false,
        can_create_tasktracker:
          response.data.can_create_tasktracker === 1 ||
          response.data.can_create_tasktracker === true ||
          false,
        can_delete_tasktracker:
          response.data.can_delete_tasktracker === 1 ||
          response.data.can_delete_tasktracker === true ||
          false,
        is_admin: response.data.is_admin === 1 || response.data.is_admin === true || false,
      };

      setUser((prev) => {
        if (prev) {
          const updatedUser = { ...prev, permissions };
          return updatedUser;
        }
        return null;
      });

      // Update localStorage with permissions
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        const updatedUserData = { ...userData, permissions };
        localStorage.setItem('user', JSON.stringify(updatedUserData));
        setAuthCookie(AUTH_USER_COOKIE, JSON.stringify(updatedUserData));
      }

      setPermissionsLoaded(true);
    } catch (error) {
      setPermissionsLoaded(true); // Mark as loaded even on error to prevent infinite loading
    }
  }, []);

  // Fetch permissions on mount AND whenever user changes
  useEffect(() => {
    if (user) {
      fetchPermissions();
    }
  }, [user?.id]); // Only depend on user.id to avoid infinite loops

  // Refresh permissions periodically (every 5 minutes) while user is logged in
  // This ensures users see permission changes granted by admins without needing to refresh
  useEffect(() => {
    if (!user) return;

    const permissionRefreshInterval = setInterval(
      () => {
        fetchPermissions();
      },
      5 * 60 * 1000
    ); // 5 minutes in milliseconds

    return () => clearInterval(permissionRefreshInterval);
  }, [user, fetchPermissions]);

  const login = useCallback(
    (newUser: User, token: string) => {
      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('token', token);
      setAuthCookie(AUTH_USER_COOKIE, JSON.stringify(newUser));
      setAuthCookie(AUTH_TOKEN_COOKIE, token);
      // Fetch permissions after login
      setTimeout(() => {
        fetchPermissions();
      }, 100);
    },
    [fetchPermissions]
  );

  const logout = useCallback(() => {
    // Portal logout returns to the central Dashboard. We deliberately do NOT
    // revoke the shared session here: the httpOnly session survives, so the
    // central login page bounces the still-authenticated user straight to
    // /dashboard — the same smooth behavior as the funnel and letters portals.
    // The real global sign-out lives on the Dashboard itself. Do NOT setUser(null)
    // here: flipping the guard to "logged out" makes it redirect back with ?next=,
    // cancelling the logout. The full-page redirect below resets all state anyway.
    clearLocalAuthState();
    // Set a cross-domain cookie so LoginPage knows NOT to auto-trigger login after logout
    setJustLoggedOutCookie();

    // Clear MSAL cache so next login asks for credentials instead of auto-signing in
    if (typeof window !== 'undefined') {
      const msalKeys = Object.keys(localStorage).filter(
        (key) =>
          key.startsWith('msal.') ||
          key.startsWith('login.') ||
          key.includes('msal') ||
          key.includes('authority')
      );
      msalKeys.forEach((key) => localStorage.removeItem(key));
      // Also clear sessionStorage MSAL entries
      const sessionKeys = Object.keys(sessionStorage).filter(
        (key) => key.startsWith('msal.') || key.includes('msal')
      );
      sessionKeys.forEach((key) => sessionStorage.removeItem(key));
    }

    // Return to the central Dashboard. The central login page bounces a still-
    // authenticated user straight to /dashboard (same behavior as funnel/letters).
    // Unconditional so it never depends on hostname detection.
    if (typeof window !== 'undefined') {
      window.location.replace(buildCentralLogoutUrl());
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    permissionsLoaded,
    login,
    logout,
    setUser,
    setIsLoading,
    fetchPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { clearJustLoggedOutCookie, getCookie, JUST_LOGGED_OUT_COOKIE };

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
